import React from "react";
import { useFormikContext } from "formik";
import { getIconForUID } from "components/FormerEditor/utils";
import { BUTTON_CLASS_CONTEXT, UI_THEME } from "utils/constants/ui";
import { COLORS } from "utils/styles";
import Button from "components/Form/fields/Button";
import IconButton from "components/IconButton";
import classnames from "classnames";
import get from "lodash/get";
import { STATE_KEYS } from "utils/constants/state";
import { usePageBuilderContext } from "utils/context";
import Analytics, { SHOW_LIVE_APPLICATION } from "utils/analytics";
import PublishConfigForm from "./PublishConfigForm";
import Overlay from "components/Overlay";
import { useCtxReferences } from "utils/selectors";
import { getManifestLiveUrl } from "utils/manifest";
import { useDisableManifestPublish } from "utils/hooks/manifest";

const DEFAULT_CLASSES = {
  container: "w-100 flex items-center"
};

const PublishActions = ({ children, customClasses, preview, publish }) => {
  const pageCtx = usePageBuilderContext();
  const tracking = pageCtx ? pageCtx.tracking : {};
  const classes = { ...DEFAULT_CLASSES, ...customClasses };
  const { values, isSubmitting, setFieldValue } = useFormikContext();
  const { application, resource } = useCtxReferences();
  const hasEntities = get(values, STATE_KEYS.MANIFEST.ENTITIES, []).length;
  const showModal = get(values, STATE_KEYS.EDITOR.PUBLISH_SHOW_MENU);
  const manifest = values && values.manifest;
  const liveURL = manifest
    ? getManifestLiveUrl({
        manifest,
        application,
        resource
      })
    : null;

  const disablePublish = useDisableManifestPublish();

  const setShowModal = (show) => {
    setFieldValue(STATE_KEYS.EDITOR.PUBLISH_SHOW_MENU, show);
  };

  const actions = [
    {
      copy: "Publish",
      disabled: disablePublish,
      theme: UI_THEME.SLIM,
      onClick: () => {
        setShowModal(true);
      }
    }
  ];

  /**
   * Note: a "preview" button could be added here later
   */
  const buttonNodes = actions.map(({ component, ...action }, actionIx) => {
    const isLast = actions.length - 1 === actionIx;
    const COMPONENT_MAP = {
      IconButton: IconButton,
      Button: Button
    };
    const ButtonComponent = COMPONENT_MAP[component] || Button;

    return (
      <div
        key={actionIx}
        className={classnames("dib", {
          pr2: !isLast
        })}
      >
        <ButtonComponent {...action} />
      </div>
    );
  });

  const Icon = getIconForUID("flash");
  if (liveURL && hasEntities) {
    const liveDisabled = isSubmitting || !hasEntities;
    const liveButtonProps = {
      disabled: liveDisabled,
      classes: {
        contextClass: BUTTON_CLASS_CONTEXT.SECONDARY
      },
      theme: UI_THEME.SLIM,
      href: liveURL,
      target: "_blank",
      tag: "a",
      children: (
        <div className="flex flex-row">
          <div className="f5 pr1">Live</div>
          <Icon
            size={18}
            color={liveDisabled ? COLORS.disabledAction : COLORS.blue}
          />
        </div>
      )
    };
    buttonNodes.splice(
      buttonNodes.length - 1,
      0,
      <div
        className="pr2"
        key="live-button"
        onClick={() => {
          Analytics.track(SHOW_LIVE_APPLICATION, {
            ...tracking,
            live_url: liveURL
          });
        }}
      >
        <Button {...liveButtonProps} />
      </div>
    );
  }

  return (
    <div className={classes.container}>
      {children}
      <div className="flex flex-row items-center">{buttonNodes}</div>
      {showModal && (
        <Overlay hideOverlay={() => setShowModal(false)}>
          <div className="pt3">
            <PublishConfigForm publish={publish} setShow={setShowModal} />
          </div>
        </Overlay>
      )}
    </div>
  );
};

export default PublishActions;
